import { IMapPin } from '../components/SpacesMap';
import { IOperatorSearchResult } from '../interfaces/heavyDutyMachinery/IOperator';
import { IAccessorySearchResult } from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { IServiceSearchResult } from '../interfaces/heavyDutyMachinery/IService';

/**
 * Pins and stable ids for the map's non-machinery categories.
 *
 * In one file rather than inside the mobile map because both maps need them
 * now -- the desktop map shows the same four categories, and a pin helper
 * living inside one screen is a pin helper the other screen quietly
 * reimplements slightly differently. Machinery's own lives beside them in
 * machineryMapPin.ts, which predates this and has its own live-status logic.
 */

// An operator or accessory search result is really (resource × the specific
// Machinery it is shown at) -- see their own doc comments: the same operator
// legitimately appears more than once, one row per qualifying machine in
// view. The bare resource id is not unique across the result set, so every
// pin, card and selection lookup keys on this composite instead.
export const operatorPinId = (operator: IOperatorSearchResult): string => `${operator.id}:${operator.machineryId}`;
export const accessoryPinId = (accessory: IAccessorySearchResult): string => `${accessory.id}:${accessory.machineryId}`;

/** A service appears once, at its own address, so its own id is enough. */
export const servicePinId = (service: IServiceSearchResult): string => service.id;

export const toOperatorMapPin = (operator: IOperatorSearchResult): IMapPin => ({
  id: operatorPinId(operator),
  latitude: operator.machineryLatitude,
  longitude: operator.machineryLongitude,
  priceLabel: `${Math.round(operator.hourlyRate)}€`,
});

export const toAccessoryMapPin = (accessory: IAccessorySearchResult): IMapPin => ({
  id: accessoryPinId(accessory),
  latitude: accessory.machineryLatitude,
  longitude: accessory.machineryLongitude,
  priceLabel: `${Math.round(accessory.pricePerDay)}€`,
});

/**
 * A service's own pin, at its own coordinates -- unlike operators and
 * accessories, which borrow a machine's.
 *
 * The label is the honest part. A quote-only service has no price to show and
 * putting one there would be a lie the guest only discovers after tapping, so
 * it gets a "€?" instead: something is priced here, and the price is the
 * answer to a conversation. Hourly shows the rate, fixed-job the whole job,
 * and a provider who does both leads with the hourly one (the smaller, more
 * comparable number -- a 1.200€ pin next to a 40€ pin says nothing true about
 * which is dearer).
 */
export const toServiceMapPin = (service: IServiceSearchResult): IMapPin => {
  const price = service.pricingMode === 'FixedJob' ? service.fixedJobPrice : service.hourlyRate ?? service.fixedJobPrice;
  return {
    id: servicePinId(service),
    latitude: service.latitude,
    longitude: service.longitude,
    priceLabel: service.requestMode === 'Quote' || price == null ? '€?' : `${Math.round(price)}€`,
  };
};
